import { useElementSize } from '@custom-react-hooks/use-element-size';
import { Box, Button, Stack, alpha, useTheme } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import CheckboxGroup from '../../checkBoxGroup/checkboxGroup';
import SelectField from '../../selectField';
import TextField from '../../textField';

type Props = {
  questions: Record<string, any>;
  userResponses: Record<string, any>;
  maxHeight: number;
  onUserResponseChange: (questionId: string, response: any) => void;
  onBack: () => void;
  onCancel: () => void;
  onNext: () => void;
};

const StepPostProcessing = ({
  questions = {},
  userResponses = {},
  maxHeight,
  onUserResponseChange,
  onBack,
  onCancel,
  onNext,
}: Props) => {
  const getQuestionDef = (qId: string) => {
    if (!questions) return null;
    if (Array.isArray(questions)) {
      for (const item of questions) {
        if (item.id === qId) return item;
        if (item.questions && Array.isArray(item.questions)) {
          const found = item.questions.find((q: any) => q.id === qId);
          if (found) return found;
        }
      }
    } else if (typeof questions === 'object') {
      return questions[qId] || null;
    }
    return null;
  };

  const { t } = useTranslation();
  const theme = useTheme();

  const [containerRef, containerSize] = useElementSize();
  const [actionPanelRef, actionPanelSize] = useElementSize();

  const [errors, setErrors] = useState<any>({});

  const setPostProcessingActions = (response: string[]) => {
    onUserResponseChange('postProcessingActions', response);
  };

  const setCustomSystemPrompt = (response: string) => {
    onUserResponseChange('customSystemPrompt', response);
  };

  const setCustomUserPrompt = (response: string) => {
    onUserResponseChange('customUserPrompt', response);
  };

  const setExternalApiSettings = (settingName: string, value: any) => {
    onUserResponseChange('externalApiSettings', { ...userResponses.externalApiSettings, [settingName]: value });
  };

  const handleNext = () => {
    const errors: any = {};
    let hasError = false;
    if (userResponses.postProcessingActions && userResponses.postProcessingActions.includes('customPrompt')) {
      if (!userResponses.customUserPrompt) {
        errors.customUserPrompt = t('common.errors.requiredField');
        hasError = true;
      }
    }
    if (userResponses.postProcessingActions && userResponses.postProcessingActions.includes('externalApi')) {
      if (!userResponses.externalApiSettings?.endpoint) {
        errors.externalApiEndpoint = t('common.errors.requiredField');
        hasError = true;
      }
      if (!userResponses.externalApiSettings?.authenticationType) {
        errors.externalApiAuthenticationType = t('common.errors.requiredField');
        hasError = true;
      }
      if (userResponses.externalApiSettings?.authenticationType === 'HttpBasic') {
        if (!userResponses.externalApiSettings?.username) {
          errors.externalApiHttpBasicUsername = t('common.errors.requiredField');
          hasError = true;
        }
        if (!userResponses.externalApiSettings?.password) {
          errors.externalApiHttpBasicPassword = t('common.errors.requiredField');
          hasError = true;
        }
      }
    }
    setErrors(errors);
    if (!hasError) {
      onNext();
    }
  };

  const handleCancel = () => {
    onCancel();
  };

  const handleBack = () => {
    onBack();
  };

  return (
    <Box
      ref={containerRef}
      sx={{
        height: `${maxHeight}px`,
        display: 'flex',
        flexDirection: 'column',
        border: 1,
        borderColor: 'divider',
        borderLeft: `3px solid ${theme.palette.secondary.main}`,
        borderRadius: '12px',
        overflow: 'hidden',
        bgcolor: 'background.paper',
      }}
    >
      <Box sx={{ flex: 1, overflowY: 'auto', p: 3 }}>
        <Stack alignItems={'flex-start'} spacing={4}>
          <CheckboxGroup
            label={(getQuestionDef('postProcessingActions')?.questionText || getQuestionDef('postProcessingActions')?.question || 'Post-Processing Actions')}
            values={userResponses.postProcessingActions || []}
            options={(getQuestionDef('postProcessingActions')?.options || [])}
            required
            error={errors.postProcessingActions}
            onChange={setPostProcessingActions}
            maxOptionsPerColumn={3}
          />
          {userResponses.postProcessingActions && userResponses.postProcessingActions.includes('customPrompt') && (
            <>
              <TextField
                label={(getQuestionDef('customSystemPrompt')?.questionText || getQuestionDef('customSystemPrompt')?.question || 'System Prompt')}
                value={userResponses.customSystemPrompt || ''}
                multiline
                rows={3}
                error={errors.customSystemPrompt}
                onChange={setCustomSystemPrompt}
              />
              <TextField
                label={(getQuestionDef('customUserPrompt')?.questionText || getQuestionDef('customUserPrompt')?.question || 'User Prompt')}
                value={userResponses.customUserPrompt || ''}
                required
                multiline
                rows={3}
                error={errors.customUserPrompt}
                onChange={setCustomUserPrompt}
              />
            </>
          )}
          {userResponses.postProcessingActions && userResponses.postProcessingActions.includes('externalApi') && (
            <>
              <TextField
                label={'Endpoint URL'}
                value={userResponses.externalApiSettings?.endpoint || ''}
                error={errors.externalApiEndpoint}
                onChange={(value) => setExternalApiSettings('endpoint', value)}
              />
              <SelectField
                label={'Authentication Type'}
                value={userResponses.externalApiSettings?.authenticationType || ''}
                options={[{label: 'None', value: 'None'}, {label: 'HTTP Basic', value: 'HttpBasic'}]}
                required
                error={errors.externalApiAuthenticationType}
                onChange={(value) => setExternalApiSettings('authenticationType', value)}
                fullWidth={false}
              />
              {userResponses.externalApiSettings?.authenticationType === 'HttpBasic' && (
                <>
                  <TextField
                    label={'Username'}
                    value={userResponses.externalApiSettings?.username || ''}
                    error={errors.externalApiHttpBasicUsername}
                    fullWidth={false}
                    onChange={(value) => setExternalApiSettings('username', value)}
                  />
                  <TextField
                    label={'Password'}
                    value={userResponses.externalApiSettings?.password || ''}
                    error={errors.externalApiHttpBasicPassword}
                    fullWidth={false}
                    onChange={(value) => setExternalApiSettings('password', value)}
                    type="password"
                  />
                </>
              )}
            </>
          )}
        </Stack>
      </Box>
      <Box
        ref={actionPanelRef}
        sx={{ px: 3, py: 1.5, borderTop: 1, borderColor: 'divider', bgcolor: alpha(theme.palette.primary.main, 0.02) }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Button
            variant="text"
            startIcon={<ArrowBackIcon />}
            onClick={onBack}
            sx={{ textTransform: 'none', fontWeight: 600, color: 'text.secondary' }}
          >
            {t('common.buttons.back')}
          </Button>
          <Stack direction="row" spacing={1.5}>
            <Button
              variant="text"
              onClick={onCancel}
              sx={{ textTransform: 'none', fontWeight: 600, color: 'text.secondary' }}
            >
              {t('common.buttons.cancel')}
            </Button>
            <Button
              variant="contained"
              color="secondary"
              endIcon={<ArrowForwardIcon />}
              onClick={handleNext}
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                borderRadius: '10px',
                px: 2.5,
                color: 'text.primary',
                boxShadow: 'none',
                '&:hover': { boxShadow: 'none' },
              }}
            >
              {t('common.buttons.next')}
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Box>
  );
};

export default StepPostProcessing;
